(() => {
  const SIZE_RE = /^(?:3XS|2XS|XXS|XS|S|M|L|XL|2XL|XXL|3XL|XXXL|4XL|5XL|6XL|7XL|\d{1,3}(?:\/\d{1,3})?)$/i;
  const MEASURE_RE = /(chest|bust|length|shoulder|sleeve|waist|hip|inseam|outseam|rise|leg|thigh|hem|width|foot)/i;
  const NUM_RE = /^\d+(?:[.,]\d+)?(?:\s*(?:in|inch|inches|cm))?$/i;
  const FOOT = 12;

  function text(cell) {
    return (cell?.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function findTable(description) {
    if (!description) return null;
    for (const table of description.querySelectorAll('table')) {
      const rows = Array.from(table.rows);
      if (rows.length < 3) continue;
      for (const header of rows.slice(0, 5)) {
        const headings = Array.from(header.cells, text);
        const sizes = headings.slice(1).map(value => value.toUpperCase().replace(/^XXL$/, '2XL').replace(/^XXXL$/, '3XL'));
        if (sizes.length < 2 || sizes.length > 20 || sizes.some(size => !SIZE_RE.test(size))) continue;
        let unit = '';
        const measurements = [];
        for (const row of rows.slice(rows.indexOf(header) + 1)) {
          const cells = Array.from(row.cells, text);
          if (cells.length !== sizes.length + 1) continue;
          if (!unit && /^(?:in|inch|inches|cm|centimet(?:er|re)s?)$/i.test(cells[1]) && cells.slice(1).every(v => v === cells[1])) {
            unit = cells[1];
            continue;
          }
          if (!MEASURE_RE.test(cells[0]) || !cells.slice(1).every(v => NUM_RE.test(v))) continue;
          measurements.push({ label: cells[0], values: cells.slice(1) });
        }
        if (measurements.length < 2) continue;
        if (!unit) {
          const contents = text(table);
          if (/\b(?:inches|inch)\b/i.test(contents)) unit = 'inches';
          else if (/\bcm\b|\bcentimet(?:er|re)s?\b/i.test(contents)) unit = 'cm';
          else if (/\b(?:inches|inch)\b/i.test(text(description))) unit = 'inches';
        }
        return { table, sizes, measurements, unit };
      }
    }
    return null;
  }

  function appendText(parent, tag, value, attrs = {}) {
    const el = document.createElement(tag);
    el.textContent = value;
    for (const [key, val] of Object.entries(attrs)) el.setAttribute(key, val);
    parent.append(el);
    return el;
  }

  function toInches(raw, unit) {
    if (!NUM_RE.test(raw) || !unit) return NaN;
    const value = Number.parseFloat(raw.replace(',', '.'));
    return /cm/i.test(unit) ? value / 2.54 : /in/i.test(unit) ? value : NaN;
  }

  function categoryFor(title, type) {
    const name = `${title} ${type}`.toLowerCase();
    if (/\b(shorts?|sweatshorts?|skorts?|bermudas?)\b/.test(name) && !/short.sleeve/.test(name)) return 'shorts';
    if (/\b(pants?|trousers?|sweatpants?|joggers?|jeans|leggings?|cargo|chinos?)\b/.test(name)) return 'pants';
    if (/\b(hoodie|sweatshirt|sweater|crewneck|pullover|fleece)\b/.test(name)) return 'hoodie';
    if (/\b(jacket|coat|outerwear|windbreaker|overshirt)\b/.test(name)) return 'outerwear';
    if (/\b(t.?shirt|tee|shirt|top|blouse|tank|jersey|crop|longsleeve)\b/.test(name)) return 'top';
    return 'other';
  }

  function sizeKey(value) {
    return value.toUpperCase().trim().replace(/^XXL$/, '2XL').replace(/^XXXL$/, '3XL');
  }

  function sizesFromProduct(guide) {
    const raw = guide.dataset.vvSizeValues || '';
    const values = raw.split('|').map(sizeKey).filter(Boolean);
    const unique = Array.from(new Set(values));
    return unique.length && unique.length <= 20 ? unique : ['One size'];
  }

  function matchingMeasurement(parsed, matcher) {
    return parsed?.measurements.find(row => matcher.test(row.label.trim().toLowerCase()));
  }

  function feet(inches) {
    const whole = Math.round(inches);
    return `${Math.floor(whole / FOOT)}′${whole % FOOT}″`;
  }

  function range(center) {
    const low = Math.round(center - 1.5);
    if (low < 55 || low > 80) return null;
    return `${feet(low)}–${feet(low + 3)}`;
  }

  function verifiedHeightRows(guide, sizes) {
    try {
      const data = JSON.parse(guide.dataset.vvCustomGuide || 'null');
      if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
      const values = sizes.map(size => {
        const raw = data[size] || data[sizeKey(size)];
        if (typeof raw !== 'string' || raw.length > 32) return null;
        const match = raw.replace(/[′’]/g, "'").replace(/[″”]/g, '"')
          .match(/(\d+)'(\d{1,2})"?\s*[–—-]\s*(\d+)'(\d{1,2})"?/);
        if (!match) return null;
        const low = Number(match[1]) * FOOT + Number(match[2]);
        const high = Number(match[3]) * FOOT + Number(match[4]);
        if (high < low || high - low > 4) return null;
        return raw;
      });
      return values.every(Boolean) ? values : null;
    } catch {
      return null;
    }
  }

  function measuredHeightRows(parsed, category, title) {
    if (!parsed || category === 'shorts' || category === 'other') return null;
    const name = title.toLowerCase();
    let row;
    let ratio;
    if (category === 'pants') {
      row = matchingMeasurement(parsed, /^(?:inseam|inside leg|inside length)$/);
      if (row) ratio = 0.45;
      else {
        row = matchingMeasurement(parsed, /^(?:length|total length|pants length|outseam|outer length)$/);
        ratio = 0.6;
      }
    } else {
      row = matchingMeasurement(parsed, /^(?:length|body length|garment length|shirt length|back length|total length)$/);
      if (category === 'top') ratio = /\bboxy\b/.test(name) ? 0.374 : /\bcrop|cropped\b/.test(name) ? null : /\boversized|loose fit|baggy\b/.test(name) ? 0.393 : 0.405;
      if (category === 'hoodie') ratio = /\bboxy\b/.test(name) ? 0.374 : /\bcrop|cropped\b/.test(name) ? null : /\boversized|loose fit|baggy\b/.test(name) ? 0.398 : 0.416;
      if (category === 'outerwear') ratio = /\bboxy\b/.test(name) ? 0.392 : 0.42;
    }
    if (!row || !ratio) return null;
    const values = row.values.map(raw => {
      const length = toInches(raw, parsed.unit);
      if (!Number.isFinite(length)) return null;
      if (category === 'pants' && (length < 24 || length > 48)) return null;
      if (category !== 'pants' && (length < 19 || length > 39)) return null;
      const center = length / ratio;
      if (center < 57 || center > 83) return null;
      return range(center);
    });
    return values.every(Boolean) ? values : null;
  }

  function generalHeightRows(sizes, title, category) {
    if (category === 'shorts') return sizes.map(() => 'Any height');
    if (category === 'other') return sizes.map(() => 'Varies');
    const name = title.toLowerCase();
    const isWomens = /\b(women|womens|woman|ladies|girls)\b/.test(name);
    const middle = isWomens ? 64 : 68.5;
    const offsets = { '3XS': -10, '2XS': -8, 'XXS': -8, XS: -4, S: -2, M: 0, L: 2, XL: 4, '2XL': 6, '3XL': 8, '4XL': 10, '5XL': 12, '6XL': 14, '7XL': 16 };
    return sizes.map(raw => {
      const size = sizeKey(raw);
      if (size === 'ONE SIZE' || /^\d{1,3}(?:\/\d{1,3})?$/.test(size)) return 'See measurements';
      const offset = offsets[size];
      if (offset == null) return 'See measurements';
      return range(middle + offset) || 'See measurements';
    });
  }

  function heightInfo(guide, sizes, parsed) {
    const title = guide.dataset.vvProductTitle || '';
    const type = guide.dataset.vvProductType || '';
    const category = categoryFor(title, type);
    const custom = verifiedHeightRows(guide, sizes);
    if (custom) return { values: custom, source: 'verified', category };
    const measured = measuredHeightRows(parsed, category, title);
    if (measured) return { values: measured, source: 'measured', category };
    return { values: generalHeightRows(sizes, title, category), source: 'general', category };
  }

  function buildTable(guide, parsed) {
    const sizes = parsed?.sizes || sizesFromProduct(guide);
    const result = heightInfo(guide, sizes, parsed);
    const fragment = document.createDocumentFragment();
    appendText(fragment, 'h4', parsed ? (guide.dataset.measurementsTitle || 'Garment measurements') : 'Height reference', { class: 'vv-size-guide__heading' });
    const scroller = document.createElement('div');
    scroller.className = 'vv-size-guide__scroll';
    scroller.tabIndex = 0;
    scroller.setAttribute('role', 'region');
    scroller.setAttribute('aria-label', 'Size and height reference');
    const table = document.createElement('table');
    table.className = 'vv-size-guide__table';
    appendText(table, 'caption', 'Garment sizes and height reference', { class: 'visually-hidden' });
    const head = document.createElement('thead');
    const headings = document.createElement('tr');
    appendText(headings, 'th', guide.dataset.measurementLabel || 'Measurement', { scope: 'col' });
    sizes.forEach(size => appendText(headings, 'th', size, { scope: 'col' }));
    head.append(headings);
    table.append(head);
    const body = document.createElement('tbody');
    const heightRow = document.createElement('tr');
    heightRow.className = 'vv-size-guide__height-row';
    appendText(heightRow, 'th', 'Height guide*', { scope: 'row' });
    result.values.forEach(label => appendText(heightRow, 'td', label));
    body.append(heightRow);
    parsed?.measurements.forEach(item => {
      const row = document.createElement('tr');
      appendText(row, 'th', item.label, { scope: 'row' });
      item.values.forEach(value => appendText(row, 'td', value));
      body.append(row);
    });
    table.append(body);
    scroller.append(table);
    fragment.append(scroller);
    let note;
    if (result.category === 'shorts') note = '*Height does not determine shorts size. Use waist and hip measurements.';
    else if (result.category === 'other') note = '*Height varies. Check the product measurements for fit.';
    else if (result.source === 'general') note = '*General height starting point only; garment length is not verified. Compare with clothing you own.';
    else if (result.source === 'measured') note = '*Rough height reference from garment length, not a guaranteed fit. Compare your measurements.';
    else note = '*Height is a starting point, not a fit guarantee. Compare garment measurements.';
    if (/\bboxy\b/i.test(guide.dataset.vvProductTitle || '')) note += ' Boxy cuts are intentionally shorter.';
    if (parsed?.unit) note += ` Measurements in ${parsed.unit}.`;
    appendText(fragment, 'p', note, { class: 'vv-size-guide__unit' });
    return fragment;
  }

  function enhance(root) {
    root.querySelectorAll('[data-vv-size-guide]').forEach(guide => {
      if (guide.dataset.vvEnhanced === 'true') return;
      const essentials = guide.closest('.vv-pdp-essentials');
      const description = essentials?.querySelector('.vv-pdp-essentials__item:first-child .vv-pdp-essentials__body');
      const mount = guide.querySelector('[data-vv-size-measurements]');
      if (!mount) return;
      const parsed = findTable(description);
      mount.replaceChildren(buildTable(guide, parsed));
      mount.hidden = false;
      const empty = guide.querySelector('[data-vv-measurement-note]');
      if (empty) empty.hidden = true;
      if (parsed && description) {
        const wrapper = parsed.table.parentElement;
        parsed.table.remove();
        if (wrapper && wrapper !== description && !wrapper.textContent.trim() && !wrapper.querySelector('img, video, iframe')) wrapper.remove();
        for (const el of description.querySelectorAll('p, h6, h5, h4, div')) {
          if (el.querySelector('table, img, video, iframe')) continue;
          if (!el.textContent.replace(/\u00a0/g, '').trim() && Array.from(el.children).every(node => node.tagName === 'BR')) el.remove();
        }
      }
      guide.dataset.vvEnhanced = 'true';
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => enhance(document), { once: true });
  else enhance(document);
  document.addEventListener('shopify:section:load', event => enhance(event.target));
})();
